import { UserPlus } from "lucide-react"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"

/**
 * OKS-17 — "Brand has zero candidates" state (UX Spec §States: Empty).
 *
 * Distinct from `CandidateNoResultsPanel` (filters returned 0 rows but
 * the brand has candidates). The discriminator is `is_empty: true`
 * (BE-supplied flag).
 *
 * Renders the shared `EmptyState` with i18n copy + the OKS-16
 * "Add candidate" CTA passed in from the parent so the dropdown
 * trigger ref + dialog plumbing stays at the page level.
 *
 * Locked testids:
 *   - `candidate-list-empty` — section root
 *   - `candidate-list-empty-cta` — Add candidate CTA inside this state
 */
interface CandidateEmptyStateProps {
  /**
   * Pre-built CTA — typically the OKS-16 `<AddCandidateMenu>` already
   * mounted on the page. Wrapped here with a `data-testid` slot so the
   * UX-Spec `candidate-list-empty-cta` lock holds. The empty state
   * doesn't own the dialog state — only renders the trigger.
   */
  cta: ReactNode
}

export function CandidateEmptyState({ cta }: CandidateEmptyStateProps) {
  const { t } = useTranslation()
  return (
    <div data-testid="candidate-list-empty">
      <EmptyState
        icon={UserPlus}
        title={t("candidates.list.empty_state.title")}
        description={t("candidates.list.empty_state.description")}
        actions={<span data-testid="candidate-list-empty-cta">{cta}</span>}
      />
    </div>
  )
}
